import React, { useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Theme, Typography } from '@mui/material';

import {
    capitalize,
    getColourFromStatus,
    prettifyLngLat,
    toTimeDDMonYearFormat,
    round,
    normaliseAngle,
} from 'utils';
import { AntennaManager } from 'models';

import GroundStationOverview from 'components/groundstations/Overview';
import LoadableImage from 'components/LoadableImage';
import RefreshButton from 'components/RefreshButton';
import AzimuthSwatch from 'components/AzimuthSwatch';
import ElevationSwatch from 'components/ElevationSwatch';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'grid',
            gap: theme.spacing(1),
        },
        antennaRoot: {
            display: 'grid',
            gap: theme.spacing(1),
        },
        topSection: {
            display: 'grid',

            gap: theme.spacing(1),
            [theme.breakpoints.down('sm')]: {
                gridTemplateRows: 'auto auto auto',
            },
            [theme.breakpoints.up('sm')]: {
                gridTemplateColumns: 'auto 1fr auto',
            },
        },
        disabled: {
            color: 'red',
            fontSize: 12,
        },
        subsection: {
            paddingLeft: theme.spacing(2),
        },
    }),
);

interface OverviewProps {
    antenna: AntennaManager.Antenna;
    onRefreshClick?: () => void;
    isRefreshing?: boolean;
    hideRefresh?: boolean;
}

const AntennaOverview: React.FunctionComponent<OverviewProps> = ({
    antenna,
    onRefreshClick,
    isRefreshing,
    hideRefresh,
}) => {
    const classes = useStyles();

    const [imgSrc, setImgSrc] = useState<string>();

    useEffect(() => {
        // antenna may update fairly often. Avoid redrawing the image.
        if (antenna.image && !imgSrc) setImgSrc(antenna.image);
    }, [antenna]);

    return (
        <div className={classes.root}>
            <div className={classes.antennaRoot}>
                <div className={classes.topSection}>
                    <LoadableImage
                        src={antenna.image}
                        width={320}
                        alt={`Image of ${antenna.name}`}
                        style={{ alignItems: 'start', justifyContent: 'start' }}
                    />
                    <div>
                        <GroundStationOverview
                            groundstation={antenna.groundstation}
                        />
                        <Typography variant="h6">
                            {antenna.name}{' '}
                            {!antenna.enabled && (
                                <span className={classes.disabled}>
                                    (disabled)
                                </span>
                            )}
                            {'  '}
                            <span
                                style={{
                                    color: getColourFromStatus(antenna.status),
                                }}
                            >
                                ({capitalize(antenna.status)})
                            </span>
                        </Typography>
                        <div className={classes.subsection}>
                            <Typography variant="subtitle1">
                                Location:{' '}
                                {prettifyLngLat([antenna.lon, antenna.lat])},
                                {'  '}
                                {antenna.altitude}m
                            </Typography>

                            <Typography variant="subtitle1">
                                Utilisation:{' '}
                                {round(antenna.percent_utilised * 100)}%
                            </Typography>

                            <Typography variant="subtitle1">
                                Azimuth:{' '}
                                {antenna.lastKnownAzimuth !== null
                                    ? `${round(
                                          normaliseAngle(
                                              antenna.lastKnownAzimuth,
                                          ),
                                          2,
                                      )}°`
                                    : 'N/A'}
                                {antenna.lastKnownAzimuth !== null && (
                                    <AzimuthSwatch
                                        rotation={antenna.lastKnownAzimuth}
                                        margin="0px 4px"
                                        size={24}
                                    />
                                )}
                            </Typography>
                            <Typography variant="subtitle1">
                                Elevation:{' '}
                                {antenna.lastKnownElevation !== null
                                    ? `${round(antenna.lastKnownElevation, 2)}°`
                                    : 'N/A'}
                                {antenna.lastKnownElevation !== null && (
                                    <ElevationSwatch
                                        elevation={antenna.lastKnownElevation}
                                        margin="0px 4px"
                                        size={24}
                                    />
                                )}
                            </Typography>

                            <Typography variant="subtitle1">
                                Last Updated:{' '}
                                {antenna.lastKnownTime
                                    ? toTimeDDMonYearFormat(
                                          antenna.lastKnownTime,
                                      )
                                    : 'N/A'}
                            </Typography>
                        </div>
                    </div>
                    <div>
                        {!hideRefresh && (
                            <RefreshButton
                                onClick={onRefreshClick}
                                isRefreshing={isRefreshing}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

AntennaOverview.displayName = 'AntennaOverview';
export default AntennaOverview;
